import { useState } from "react";
import { toast } from "sonner";
import { completeUploadingApplicationDetails } from "../../api/discussion";

type UploadingApplicationDetailsViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

/**
 * Uploading Application Details - Filing step view
 * Figma: Frame 1000005758 — "Upload to Portal" with IP India instructions
 * Pixel-perfect: 22px gap, 20px gaps, Inter 16px/12px, #222222
 */
export default function UploadingApplicationDetailsView({
  leadId,
  onSuccess,
}: UploadingApplicationDetailsViewProps) {
  const [saving, setSaving] = useState(false);

  const handleSaveAndContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    setSaving(true);
    try {
      await completeUploadingApplicationDetails(leadId);
      toast.success("Application details saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this step.
        </p>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col items-center w-full"
      style={{
        fontFamily: "Inter, sans-serif",
        gap: 22,
        fontFeatureSettings: "'ss01' on, 'cv01' on",
      }}
    >
      {/* Frame 1000005753 - outer column, gap 20px */}
      <div
        className="flex flex-col items-center w-full max-w-[1085px]"
        style={{ gap: 20 }}
      >
        {/* Upload to Portal - 16px Inter, #222222, line-height 20px */}
        <div
          className="flex items-center text-[#222222]"
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 400,
            fontSize: 16,
            lineHeight: "20px",
            fontFeatureSettings: "'ss01' on, 'cv01' on",
          }}
        >
          Upload to Portal
        </div>

        {/* Frame 1000005759 - description row, centered, gap 20px */}
        <div
          className="flex flex-row justify-center items-center w-full"
          style={{ gap: 20 }}
        >
          {/* Frame 1000005750 - body text container */}
          <div
            className="flex flex-col justify-center items-center"
            style={{
              gap: 20,
              maxWidth: 619,
            }}
          >
            <p
              className="flex items-center text-center text-[#222222]"
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 400,
                fontSize: 12,
                lineHeight: "20px",
                fontFeatureSettings: "'ss01' on, 'cv01' on",
              }}
            >
              Log in to the IP India Trademark Portal and upload the scanned
              documents along with the application details.
            </p>
          </div>
        </div>

        {/* Save & Continue is provided by the parent footer */}
        <form
          id="uploading-application-details-form"
          onSubmit={handleSaveAndContinue}
          className="sr-only"
          aria-hidden
        />
      </div>
    </div>
  );
}
